import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/shared/components/button';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Browser extensions (esp. Google Translate) mutate the DOM and can trigger
 * React commit errors like insertBefore/removeChild. Those are not real app bugs.
 */
export function isBrowserExtensionDomError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message;
  return (
    message.includes("Failed to execute 'insertBefore' on 'Node'") ||
    message.includes("Failed to execute 'removeChild' on 'Node'") ||
    message.includes('The node before which the new node is to be inserted') ||
    message.includes('The node to be removed is not a child of this node')
  );
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isBrowserExtensionDomError(error)) {
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isBrowserExtensionDomError(error)) {
      if (import.meta.env.DEV) {
        console.warn(
          'Ignored browser-extension DOM mutation error:',
          error.message,
        );
      }
      return;
    }
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center px-4 text-center">
          <div className="bg-danger/15 mb-4 flex h-16 w-16 items-center justify-center rounded-full">
            <svg
              className="h-8 w-8 text-danger"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-body">
            Application Error
          </h2>
          <p className="mt-2 max-w-md text-sm text-secondary">
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </p>
          <div className="mt-6 flex gap-3">
            <Button variant="outline" onClick={this.handleReset}>
              Try again
            </Button>
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
